"use client";

import { DEFAULT_ITEMS } from "@/assets/static/navigation_bar_items";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

export type NavigationItem = {
	label: string;
	href: string;
};

type NavigationItemsContextValue = {
	items: NavigationItem[];
	isDefault: boolean;
	setItems: (items: NavigationItem[]) => void;
	reset: () => void;
};

const NavigationItemsContext = createContext<NavigationItemsContextValue | null>(null);

export function NavigationItemsProvider({ children }: { children: React.ReactNode }) {
	const [items, setItemsState] = useState<NavigationItem[]>(DEFAULT_ITEMS);
	const [isDefault, setIsDefault] = useState(true);

	const setItems = useCallback((nextItems: NavigationItem[]) => {
		setItemsState(nextItems);
		setIsDefault(false);
	}, []);

	const reset = useCallback(() => {
		setItemsState(DEFAULT_ITEMS);
		setIsDefault(true);
	}, []);

	const value = useMemo<NavigationItemsContextValue>(() => ({
		items,
		isDefault,
		setItems,
		reset,
	}), [items, isDefault, setItems, reset]);

	return (
		<NavigationItemsContext.Provider value={value}>
			{children}
		</NavigationItemsContext.Provider>
	);
}

export function useNavigationItems() {
	const ctx = useContext(NavigationItemsContext);
	if (!ctx) {
		throw new Error("useNavigationItems must be used within NavigationItemsProvider");
	}
	return ctx;
}
